/*
 * TASK COMPOSER DIALOG
 *
 * Shared task-feature form used by global task creation and Tasks-page create,
 * edit, read-only, dialog, and panel workflows.
 *
 * Implementation notes:
 * - Owns task option loading, validation, persistence, and success feedback.
 * - Keeps presentation-specific chrome separate while sharing one form body.
 *
 * Recent changes:
 * - Accepts a chat-provided initial draft and maps its title to the maintained first-line Description convention.
 * - Derives create and edit task titles from Description line one without duplicating legacy titles.
 * - Requires a valid due date before creating or updating an editable task.
 * - Uses the shared Account-or-Contact typeahead for creation and editable task updates.
 * - Extracted from the Tasks page so global and page entry points stay in sync.
 * - Aligns Priority with a searchable multi-assignee field and adds a cross-browser full-field due-date picker affordance.
 * - Places the assignee options above the trigger when its nearest clipping boundary lacks room below.
 * - Accepts optional Account/Contact context for route-preserving task creation.
 * - Presents Account/Contact before joined Title and Notes fields in a responsive two-section task layout.
 * - Routes suggested-contact linking through the canonical contact cache invalidation path.
 * - Sends the default-on assignment-email choice with editable composer saves and displays backend queue outcomes.
 * - Aligns the creation/edit email choice left of the footer actions and surfaces backend queue outcomes.
 * - Reuses shared segmented Priority, due-date shortcuts, and task footer controls across dialog and panel presentations.
 * - Resolves selected Prospect/Commercial account audiences before exposing direct-task assignees.
 * - Clears prior account assignees and rejects Save while a newly selected account audience is unresolved.
 */

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { THEME_CLASSES } from '../../constants/themeConstants';
import {
  Task,
  TaskPriority,
  TaskStatus,
  TASK_DESCRIPTION_TITLE_REQUIRED_ERROR,
  TASK_DUE_DATE_PAST_ERROR,
  TASK_DUE_DATE_REQUIRED_ERROR,
  canMutateTask,
  getTaskDueDateValidationError,
  getTaskStatusLabel,
  getTaskTitleFromDescription,
  getTodayDateValue,
} from '../../models/Task';
import { request } from '../../services/dataService';
import {
  getAccountAudienceMemberIds,
  getAccountAudienceTeamIds,
} from '../../utils/accountAssigneeAudience';
import { createTask, updateTaskWithNotification } from '../../services/taskService';
import EntityTypeahead, { EntityTypeaheadResult } from '../common/EntityTypeahead';
import { Button, DialogShell, Input } from '../primitives';
import { useLinkContactMutation } from '../../query/contactQueries';
import { showTaskNotificationOutcome } from './taskNotificationToast';
import {
  TaskDueDateField,
  TaskPopupFooter,
  TaskPopupLayout,
  TaskPriorityControl,
  TaskTextFields,
  TASK_POPUP_CLOSE_CLASS_NAME,
  TASK_POPUP_HEADER_CLASS_NAME,
  TASK_POPUP_TITLE_CLASS_NAME,
  composeTaskDescription,
  splitTaskDescription,
} from './popup';

interface UserOption {
  id: number;
  name: string;
  email?: string;
}

interface TaskEntitySelection extends EntityTypeaheadResult {
  type: 'Account' | 'Contact';
}

export type TaskComposerInitialEntity = Pick<
TaskEntitySelection,
'id' | 'name' | 'type' | 'accountId' | 'accountName'
>;

export interface TaskComposerInitialDraft {
  title: string;
  description?: string;
  dueDate?: string;
  priority?: TaskPriority;
}

export interface TaskComposerDialogProps {
  open: boolean;
  currentUserId: number | null;
  task: Task | null;
  initialEntity?: TaskComposerInitialEntity | null;
  initialDraft?: TaskComposerInitialDraft | null;
  onTaskSaved: (task: Task) => void;
  onClose: () => void;
  presentation?: 'dialog' | 'panel';
}

const TASK_STATUSES: TaskStatus[] = ['Open', 'Waiting', 'Done', 'Canceled'];
const SUGGESTED_CONTACT_ID_BASE = 99000000;
const TASK_ENTITY_REQUIRED_ERROR = 'Select an account or contact.';
const TASK_ENTITY_RESOLUTION_ERROR = 'Unable to determine an account for the selected contact.';
const TASK_ASSIGNEE_AUDIENCE_LOADING_ERROR = 'Wait for the selected account assignees to load.';
// Popup chrome, the search field, and three typical assignee rows.
const ASSIGNEE_DROPDOWN_MINIMUM_SPACE = 220;
const ASSIGNEE_DROPDOWN_GAP = 8;

type AssigneeDropdownPlacement = 'top' | 'bottom';
type AccountAudienceStatus = 'unrestricted' | 'loading' | 'restricted' | 'error';

class TaskEntityResolutionError extends Error {}

const getTaskDescriptionDraft = (task: Task): string => {
  const title = task.title.trim();
  const description = task.description.trim();

  if (!description) {
    return title;
  }

  return getTaskTitleFromDescription(description) === title
    ? description
    : `${title}\n${description}`;
};

const getTaskEntitySelection = (task: Task): TaskEntitySelection => task.contactId
  ? {
      id: task.contactId,
      name: task.contactName ?? `Contact #${task.contactId}`,
      type: 'Contact',
      accountId: task.accountId,
      accountName: task.accountName,
    }
  : {
      id: task.accountId,
      name: task.accountName,
      type: 'Account',
      accountId: task.accountId,
      accountName: task.accountName,
    };

const getAssigneeDropdownBoundary = (element: HTMLElement) => {
  let ancestor = element.parentElement;

  while (ancestor) {
    const styles = window.getComputedStyle(ancestor);
    const overflow = `${styles.overflow} ${styles.overflowY}`;
    if (/(auto|scroll|hidden|clip)/.test(overflow)) {
      const rect = ancestor.getBoundingClientRect();
      return {
        top: Math.max(0, rect.top),
        bottom: Math.min(window.innerHeight, rect.bottom),
      };
    }
    ancestor = ancestor.parentElement;
  }

  return { top: 0, bottom: window.innerHeight };
};

const TaskComposerDialog: FC<TaskComposerDialogProps> = ({
  open,
  currentUserId,
  task,
  initialEntity = null,
  initialDraft = null,
  onTaskSaved,
  onClose,
  presentation = 'dialog',
}) => {
  const [taskTitle, setTaskTitle] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<TaskEntitySelection | null>(null);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [status, setStatus] = useState<TaskStatus>('Open');
  const [sendEmail, setSendEmail] = useState(true);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [accountAudienceStatus, setAccountAudienceStatus] = useState<AccountAudienceStatus>('unrestricted');
  const [resolvedAccountAudienceId, setResolvedAccountAudienceId] = useState<number | null>(null);
  const [accountAudienceMemberIds, setAccountAudienceMemberIds] = useState<number[]>([]);
  const [assigneeAudienceError, setAssigneeAudienceError] = useState<string | null>(null);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [assigneeDropdownPlacement, setAssigneeDropdownPlacement] = useState<AssigneeDropdownPlacement>('bottom');
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const linkContact = useLinkContactMutation();
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const hasLoadedOptionsRef = useRef(false);
  const minDueDate = useMemo(() => getTodayDateValue(), []);
  const isEditing = Boolean(task);
  const canEditTask = task ? canMutateTask(task, currentUserId) : true;
  const selectedDirectAccountId = !isEditing && selectedEntity?.type === 'Account'
    ? selectedEntity.id
    : null;
  const isAudienceResolvedForSelectedAccount = selectedDirectAccountId !== null
    && resolvedAccountAudienceId === selectedDirectAccountId;
  const hasAccountAudienceRestriction = selectedDirectAccountId !== null
    && (!isAudienceResolvedForSelectedAccount || accountAudienceStatus !== 'unrestricted');
  const hasResolvedAccountAudienceRestriction = isAudienceResolvedForSelectedAccount
    && (accountAudienceStatus === 'restricted' || accountAudienceStatus === 'error');

  const directTaskUsers = useMemo(() => {
    if (!hasAccountAudienceRestriction) {
      return users;
    }

    if (accountAudienceStatus !== 'restricted' || !isAudienceResolvedForSelectedAccount) {
      return [];
    }

    const memberIds = new Set(accountAudienceMemberIds);
    return users.filter((user) => user.id === currentUserId || memberIds.has(user.id));
  }, [accountAudienceMemberIds, accountAudienceStatus, currentUserId, hasAccountAudienceRestriction, isAudienceResolvedForSelectedAccount, users]);

  const updateAssigneeDropdownPlacement = useCallback(() => {
    const dropdown = assigneeDropdownRef.current;
    if (!dropdown) {
      return;
    }

    const triggerRect = dropdown.getBoundingClientRect();
    const boundary = getAssigneeDropdownBoundary(dropdown);
    const spaceBelow = Math.max(0, boundary.bottom - triggerRect.bottom - ASSIGNEE_DROPDOWN_GAP);
    const spaceAbove = Math.max(0, triggerRect.top - boundary.top - ASSIGNEE_DROPDOWN_GAP);
    const nextPlacement = spaceBelow < ASSIGNEE_DROPDOWN_MINIMUM_SPACE && spaceAbove > spaceBelow
      ? 'top'
      : 'bottom';

    setAssigneeDropdownPlacement((currentPlacement) => (
      currentPlacement === nextPlacement ? currentPlacement : nextPlacement
    ));
  }, []);

  const assigneeOptions = useMemo(() => {
    const optionsById = new Map<number, UserOption>();
    const addOption = (option: UserOption) => {
      if (Number.isFinite(option.id) && option.id > 0 && !optionsById.has(option.id)) {
        optionsById.set(option.id, option);
      }
    };

    (isEditing ? users : directTaskUsers).forEach(addOption);
    task?.assignees.forEach(addOption);
    task?.assigneeIds.forEach((assigneeId, index) => {
      addOption({ id: assigneeId, name: task.assigneeNames[index] ?? `User #${assigneeId}` });
    });
    if (isEditing || !hasAccountAudienceRestriction) {
      selectedAssigneeIds.forEach((assigneeId) => {
        addOption({ id: assigneeId, name: `User #${assigneeId}` });
      });
    }
    return Array.from(optionsById.values());
  }, [directTaskUsers, hasAccountAudienceRestriction, isEditing, selectedAssigneeIds, task, users]);

  const filteredAssigneeOptions = useMemo(() => {
    const normalizedQuery = assigneeQuery.trim().toLocaleLowerCase();
    if (!normalizedQuery) {
      return assigneeOptions;
    }

    return assigneeOptions.filter((assignee) => (
      assignee.name.toLocaleLowerCase().includes(normalizedQuery)
      || assignee.email?.toLocaleLowerCase().includes(normalizedQuery)
    ));
  }, [assigneeOptions, assigneeQuery]);

  const selectedAssigneeLabel = selectedAssigneeIds.length
    ? selectedAssigneeIds.map((assigneeId) =>
      assigneeOptions.find((assignee) => assignee.id === assigneeId)?.name ?? `User #${assigneeId}`
    ).join(', ')
    : 'Select assignees';

  useEffect(() => {
    if (!open) {
      return;
    }

    const initialCreationDescription = initialDraft
      ? [initialDraft.title.trim(), initialDraft.description?.trim()].filter(Boolean).join('\n')
      : '';
    const initialFields = splitTaskDescription(task ? getTaskDescriptionDraft(task) : initialCreationDescription);
    setTaskTitle(initialFields.title);
    setTaskNotes(initialFields.notes);
    setSelectedEntity(task ? getTaskEntitySelection(task) : initialEntity);
    setSelectedAssigneeIds(task?.assigneeIds ?? (currentUserId ? [currentUserId] : []));
    setDueDate(task?.dueDate ?? initialDraft?.dueDate ?? '');
    setPriority(task?.priority ?? initialDraft?.priority ?? 'Medium');
    setStatus(task?.status ?? 'Open');
    setSendEmail(true);
    setIsAssigneeDropdownOpen(false);
    setAssigneeQuery('');
    setError(null);

    if (hasLoadedOptionsRef.current) {
      setIsLoadingOptions(false);
      return;
    }

    let isMounted = true;
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const usersResponse = await request(API_ENDPOINTS.GET_USERS);
        if (!isMounted) {
          return;
        }
        setUsers((Array.isArray(usersResponse) ? usersResponse : []).map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
        })));
        hasLoadedOptionsRef.current = true;
      } catch (loadError) {
        if (!isMounted) {
          return;
        }
        console.error('Failed to load task composer options:', loadError);
        setError('Unable to load task account and assignee options.');
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    void loadOptions();
    return () => {
      isMounted = false;
    };
  }, [initialDraft, initialEntity, open, currentUserId, task]);

  useEffect(() => {
    if (!open || selectedDirectAccountId === null) {
      setAccountAudienceStatus('unrestricted');
      setResolvedAccountAudienceId(null);
      setAccountAudienceMemberIds([]);
      setAssigneeAudienceError(null);
      return;
    }

    let isMounted = true;
    setAccountAudienceStatus('loading');
    setAccountAudienceMemberIds([]);
    setAssigneeAudienceError(null);

    const loadAccountAudience = async () => {
      try {
        const [account, teams] = await Promise.all([
          request(API_ENDPOINTS.GET_ACCOUNT, { id: selectedDirectAccountId }),
          request(API_ENDPOINTS.GET_TEAMS),
        ]);
        if (!isMounted) {
          return;
        }

        const accountTeamId = Number(account?.teamId);
        const resolvedTeams = (Array.isArray(teams) ? teams : [])
          .filter((team) => typeof team.teamId === 'number' && typeof team.teamName === 'string')
          .map((team) => ({ teamId: team.teamId, teamName: team.teamName }));
        if (!Number.isInteger(accountTeamId) || !Array.isArray(teams)) {
          throw new Error('Invalid account audience response');
        }

        const audienceTeamIds = getAccountAudienceTeamIds([accountTeamId], resolvedTeams);
        if (!audienceTeamIds.size) {
          setAccountAudienceStatus('unrestricted');
          setResolvedAccountAudienceId(selectedDirectAccountId);
          return;
        }

        const memberships = await request(API_ENDPOINTS.GET_TEAMS_USERS);
        if (!isMounted) {
          return;
        }
        if (!Array.isArray(memberships)) {
          throw new Error('Invalid account audience membership response');
        }

        setAccountAudienceMemberIds(getAccountAudienceMemberIds(
          audienceTeamIds,
          memberships.map((membership) => ({
            userId: typeof membership.userId === 'number' ? membership.userId : null,
            teamId: typeof membership.teamId === 'number' ? membership.teamId : null,
          }))
        ));
        setAccountAudienceStatus('restricted');
        setResolvedAccountAudienceId(selectedDirectAccountId);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }
        console.error('Failed to load direct task account audience:', loadError);
        setAccountAudienceStatus('error');
        setResolvedAccountAudienceId(selectedDirectAccountId);
        setAssigneeAudienceError('Unable to load account audience assignees right now.');
      }
    };

    void loadAccountAudience();
    return () => {
      isMounted = false;
    };
  }, [open, selectedDirectAccountId]);

  useEffect(() => {
    if (
      isEditing
      || selectedDirectAccountId === null
      || isAudienceResolvedForSelectedAccount
      || (accountAudienceStatus !== 'restricted' && accountAudienceStatus !== 'error')
    ) {
      return;
    }

    setSelectedAssigneeIds((previousIds) => (
      currentUserId === null ? [] : previousIds.filter((id) => id === currentUserId)
    ));
  }, [accountAudienceStatus, currentUserId, isAudienceResolvedForSelectedAccount, isEditing, selectedDirectAccountId]);

  useEffect(() => {
    if (isEditing || !hasResolvedAccountAudienceRestriction) {
      return;
    }

    const eligibleAssigneeIds = new Set(directTaskUsers.map(({ id }) => id));
    setSelectedAssigneeIds((previousIds) => previousIds.filter((id) => eligibleAssigneeIds.has(id)));
  }, [directTaskUsers, hasResolvedAccountAudienceRestriction, isEditing]);

  useEffect(() => {
    if (!open || !isAssigneeDropdownOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!assigneeDropdownRef.current?.contains(event.target as Node)) {
        setIsAssigneeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isAssigneeDropdownOpen, open]);

  useEffect(() => {
    if (!isAssigneeDropdownOpen) {
      setAssigneeQuery('');
    }
  }, [isAssigneeDropdownOpen]);

  useEffect(() => {
    if (!open || !isAssigneeDropdownOpen) {
      return;
    }

    updateAssigneeDropdownPlacement();
    window.addEventListener('resize', updateAssigneeDropdownPlacement);
    window.addEventListener('scroll', updateAssigneeDropdownPlacement, true);
    return () => {
      window.removeEventListener('resize', updateAssigneeDropdownPlacement);
      window.removeEventListener('scroll', updateAssigneeDropdownPlacement, true);
    };
  }, [isAssigneeDropdownOpen, open, updateAssigneeDropdownPlacement]);

  const handleEntitySelect = (result: EntityTypeaheadResult) => {
    if (result.type !== 'Account' && result.type !== 'Contact') {
      return;
    }

    setSelectedEntity({
      ...result,
      type: result.type,
    });
    if (error === TASK_ENTITY_REQUIRED_ERROR || error === TASK_ENTITY_RESOLUTION_ERROR) {
      setError(null);
    }
  };

  const resolveSelectedEntity = async (): Promise<{ accountId: number; contactId: number | null }> => {
    if (!selectedEntity) {
      throw new TaskEntityResolutionError(TASK_ENTITY_REQUIRED_ERROR);
    }

    if (selectedEntity.type === 'Account') {
      if (!Number.isFinite(selectedEntity.id) || selectedEntity.id <= 0) {
        throw new TaskEntityResolutionError(TASK_ENTITY_RESOLUTION_ERROR);
      }
      return {
        accountId: selectedEntity.id,
        contactId: null,
      };
    }

    let contactId = selectedEntity.id;
    if (contactId >= SUGGESTED_CONTACT_ID_BASE) {
      const personId = selectedEntity.personId || contactId - SUGGESTED_CONTACT_ID_BASE;
      if (!personId) {
        throw new TaskEntityResolutionError(TASK_ENTITY_RESOLUTION_ERROR);
      }

      const linkedContact = await linkContact.mutateAsync({ personId });
      contactId = Number(linkedContact?.id);
    }

    if (!Number.isFinite(contactId) || contactId <= 0 || contactId >= SUGGESTED_CONTACT_ID_BASE) {
      throw new TaskEntityResolutionError(TASK_ENTITY_RESOLUTION_ERROR);
    }

    let accountId = Number(selectedEntity.accountId);
    if (!Number.isFinite(accountId) || accountId <= 0 || selectedEntity.id >= SUGGESTED_CONTACT_ID_BASE) {
      const persistedContact = await request(API_ENDPOINTS.GET_CONTACT, { id: contactId });
      accountId = Number(persistedContact?.accountId);
    }

    if (!Number.isFinite(accountId) || accountId <= 0) {
      throw new TaskEntityResolutionError(TASK_ENTITY_RESOLUTION_ERROR);
    }

    return { accountId, contactId };
  };

  const handleSave = async () => {
    const normalizedTitle = getTaskTitleFromDescription(taskTitle);
    if (!normalizedTitle) {
      setError(TASK_DESCRIPTION_TITLE_REQUIRED_ERROR);
      return;
    }
    if (!selectedEntity) {
      setError(TASK_ENTITY_REQUIRED_ERROR);
      return;
    }
    if (!isEditing && selectedDirectAccountId !== null && !isAudienceResolvedForSelectedAccount) {
      setError(TASK_ASSIGNEE_AUDIENCE_LOADING_ERROR);
      return;
    }
    if (!selectedAssigneeIds.length) {
      setError('Select at least one assignee.');
      return;
    }
    const dueDateValidationError = getTaskDueDateValidationError(dueDate, minDueDate);
    if (dueDateValidationError) {
      setError(dueDateValidationError);
      return;
    }
    if (isEditing && !canEditTask) {
      setError('You can only edit tasks you created or are assigned to.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const description = composeTaskDescription(taskTitle, taskNotes);
      let savedTask: Task;
      if (isEditing && task) {
        const entityIds = await resolveSelectedEntity();
        const updateResult = await updateTaskWithNotification(task.id, {
          title: normalizedTitle,
          description,
          accountId: entityIds.accountId,
          contactId: entityIds.contactId,
          dueDate: dueDate || null,
          priority,
          status,
          assigneeIds: selectedAssigneeIds,
          sendEmail,
        });
        savedTask = updateResult.task;

        showTaskNotificationOutcome(updateResult.notification);
      } else {
        const entityIds = await resolveSelectedEntity();
        const creationResult = await createTask({
          title: normalizedTitle,
          description,
          accountId: entityIds.accountId,
          contactId: entityIds.contactId,
          dueDate: dueDate || null,
          priority,
          sourceType: 'Manual',
          assigneeIds: selectedAssigneeIds,
          sendEmail,
        });
        savedTask = creationResult.task;

        showTaskNotificationOutcome(creationResult.notification);
      }

      onTaskSaved(savedTask);
      toast.success(isEditing ? `Task "${savedTask.title}" updated.` : `Task "${savedTask.title}" created.`);
      onClose();
    } catch (saveError) {
      if (saveError instanceof TaskEntityResolutionError) {
        setError(saveError.message);
      } else {
        console.error(`Failed to ${isEditing ? 'update' : 'create'} task:`, saveError);
        setError(`Unable to ${isEditing ? 'update' : 'create'} this task right now.`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) {
    return null;
  }

  const composerTitle = isEditing ? (canEditTask ? 'Edit Task' : 'View Task') : 'New task';
  const formContent = (
    <div className="p-5 sm:p-6">
      <TaskPopupLayout
        stacked={presentation === 'panel'}
        primary={(
          <>
            <div>
              <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`}>
                Account or Contact
              </label>
              {selectedEntity ? (
                <div className="flex min-h-10 items-center gap-2">
                  <div className={`flex min-w-0 items-center rounded-lg px-3 py-2 text-sm ${
                    selectedEntity.type === 'Account'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200'
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-200'
                  }`}>
                    <span className="truncate">{selectedEntity.name}</span>
                    {canEditTask && (
                      <Button
                        onClick={() => {
                          setSelectedEntity(null);
                          setError(null);
                        }}
                        variant="icon"
                        className="ml-2 h-6 w-6 rounded-full bg-transparent hover:bg-transparent"
                        aria-label={`Remove ${selectedEntity.name}`}
                      >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              ) : canEditTask ? (
                <EntityTypeahead
                  placeholder="Search accounts and contacts..."
                  onSelect={handleEntitySelect}
                  className="w-full"
                />
              ) : null}
            </div>

            <TaskTextFields
              idPrefix="task-create"
              title={taskTitle}
              notes={taskNotes}
              onTitleChange={(value) => {
                setTaskTitle(value);
                if (error === TASK_DESCRIPTION_TITLE_REQUIRED_ERROR && getTaskTitleFromDescription(value)) {
                  setError(null);
                }
              }}
              onNotesChange={setTaskNotes}
              disabled={!canEditTask}
              invalid={error === TASK_DESCRIPTION_TITLE_REQUIRED_ERROR}
              notesRows={presentation === 'panel' ? 6 : 10}
            />
          </>
        )}
        details={(
          <>
            <TaskPriorityControl
              idPrefix="task-create"
              value={priority}
              onChange={setPriority}
              disabled={!canEditTask}
            />

            <div>
              <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`} htmlFor="task-create-assignee">
                Assignee
              </label>
              <div ref={assigneeDropdownRef} className="relative">
                <button
                  id="task-create-assignee"
                  type="button"
                  onClick={() => {
                    if (!canEditTask) return;
                    if (!isAssigneeDropdownOpen) updateAssigneeDropdownPlacement();
                    setIsAssigneeDropdownOpen((isOpen) => !isOpen);
                  }}
                  className={`flex min-h-10 w-full items-center justify-between gap-3 rounded-xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} px-3 py-2 text-left text-sm ${THEME_CLASSES.input.text} focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-100`}
                  disabled={!canEditTask}
                  aria-haspopup="true"
                  aria-expanded={isAssigneeDropdownOpen}
                >
                  <span className="min-w-0 truncate">{selectedAssigneeLabel}</span>
                  <ChevronDownIcon className={`h-4 w-4 shrink-0 ${THEME_CLASSES.text.secondary}`} aria-hidden="true" />
                </button>
                {canEditTask && isAssigneeDropdownOpen && (
                  <div
                    className={`absolute left-0 right-0 z-20 rounded-xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} p-2 shadow-lg ${assigneeDropdownPlacement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}`}
                    role="group"
                    aria-label="Assignee options"
                    data-assignee-dropdown-placement={assigneeDropdownPlacement}
                  >
                    <div className="relative mb-2">
                      <MagnifyingGlassIcon className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${THEME_CLASSES.text.secondary}`} aria-hidden="true" />
                      <Input type="search" value={assigneeQuery} onChange={(event) => setAssigneeQuery(event.target.value)} placeholder="Search assignees..." aria-label="Search assignees" className="pl-9" autoFocus />
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {filteredAssigneeOptions.map((user) => (
                        <label key={user.id} className={`flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2 text-sm ${THEME_CLASSES.text.primary} hover:bg-slate-100 dark:hover:bg-slate-900`}>
                          <input
                            type="checkbox"
                            checked={selectedAssigneeIds.includes(user.id)}
                            onChange={() => setSelectedAssigneeIds((previousIds) => previousIds.includes(user.id)
                              ? previousIds.filter((selectedId) => selectedId !== user.id)
                              : [...previousIds, user.id])}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="min-w-0">
                            <span className="block truncate">{user.name}</span>
                            {user.email && <span className={`block truncate text-xs ${THEME_CLASSES.text.secondary}`}>{user.email}</span>}
                          </span>
                        </label>
                      ))}
                      {!filteredAssigneeOptions.length && <p className={`px-3 py-4 text-center text-sm ${THEME_CLASSES.text.secondary}`}>No assignees found.</p>}
                    </div>
                  </div>
                )}
              </div>
              {assigneeAudienceError && <p className="mt-2 text-xs text-red-500" role="alert">{assigneeAudienceError}</p>}
            </div>

            {isEditing && (
              <div>
                <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`} htmlFor="task-create-status">Status</label>
                <select id="task-create-status" value={status} onChange={(event) => setStatus(event.target.value as TaskStatus)} className={`w-full rounded-xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-2 text-sm`} disabled={!canEditTask}>
                  {TASK_STATUSES.map((taskStatus) => <option key={taskStatus} value={taskStatus}>{getTaskStatusLabel(taskStatus)}</option>)}
                </select>
              </div>
            )}

            <TaskDueDateField
              id="task-create-due-date"
              min={minDueDate}
              value={dueDate}
              onChange={(value) => {
                setDueDate(value);
                if ((error === TASK_DUE_DATE_REQUIRED_ERROR || error === TASK_DUE_DATE_PAST_ERROR) && !getTaskDueDateValidationError(value, minDueDate)) setError(null);
              }}
              disabled={!canEditTask}
              invalid={error === TASK_DUE_DATE_REQUIRED_ERROR || error === TASK_DUE_DATE_PAST_ERROR}
              iconTestId="task-due-date-icon"
            />
          </>
        )}
      />

      {error && <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">{error}</div>}
      {isEditing && !canEditTask && (
        <div className={`mt-5 rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm ${THEME_CLASSES.text.secondary}`}>
          You can view this task, but only the creator or an assignee can edit it.
        </div>
      )}

      <TaskPopupFooter
        testId="task-composer-actions"
        checked={sendEmail}
        onCheckedChange={setSendEmail}
        showNotification={canEditTask}
        actions={(
          <>
            <Button onClick={onClose} variant="secondary" size="sm">{canEditTask ? 'Cancel' : 'Close'}</Button>
            {canEditTask && (
              <Button onClick={handleSave} size="sm" disabled={isSaving || (!isEditing && isLoadingOptions)}>
                {isSaving ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Task' : 'Create task')}
              </Button>
            )}
          </>
        )}
      />
    </div>
  );

  if (presentation === 'panel') {
    return (
      <aside className={`flex min-h-0 flex-col overflow-hidden rounded-lg border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} shadow-sm`} aria-labelledby="task-editor-panel-title">
        <div className={`flex items-start justify-between gap-3 border-b ${THEME_CLASSES.card.border} px-5 py-4`}>
          <div className="min-w-0">
            <h2 id="task-editor-panel-title" className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>{composerTitle}</h2>
            {task && <p className={`mt-1 truncate text-sm ${THEME_CLASSES.text.secondary}`}>{task.title}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${THEME_CLASSES.text.secondary} transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-slate-900 dark:hover:text-slate-50`}
            aria-label="Close task panel"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{formContent}</div>
      </aside>
    );
  }

  return (
    <DialogShell
      open={open}
      title={composerTitle}
      onClose={onClose}
      panelClassName="!w-[calc(100vw-1.5rem)] !max-w-5xl !rounded-2xl sm:!w-[calc(100vw-3rem)]"
      headerClassName={TASK_POPUP_HEADER_CLASS_NAME}
      titleClassName={TASK_POPUP_TITLE_CLASS_NAME}
      closeButtonClassName={TASK_POPUP_CLOSE_CLASS_NAME}
      bodyPadding={false}
    >
      {formContent}
    </DialogShell>
  );
};

export default TaskComposerDialog;
